import api from '../../../api/apiClient';

/**
 * Work assignment and site visits — `AnalystController` for the assigning side,
 * `EmployeeApiController` for the employee's own visits.
 */

const unwrap = async <T>(promise: Promise<{ data: T }>): Promise<T> => (await promise).data;

const toArray = <T>(body: any): T[] => {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  return [];
};

/**
 * `AnalystController` wraps a job's own collections one level deeper than the flat lists —
 * `{ data: { job_id, tasks | visits | files } }` — so the list has to be named to be found.
 * Reading `data` alone yields an object, which `toArray` rightly refuses.
 */
const toNestedArray = <T>(body: any, key: string): T[] => toArray<T>(body?.data?.[key] ?? body);

export interface AssignableEmployee {
  id?: number;
  employee_id?: number;
  name?: string;
  department?: string;
  position?: string;
  [key: string]: unknown;
}

export interface SiteVisit {
  id?: number;
  job_id?: number;
  job_name?: string;
  site_address?: string;
  assigned_date?: string;
  assigned_shift?: string;
  status?: string;
  site_engineer?: string;
  [key: string]: unknown;
}

export interface AnalystJob {
  id?: number;
  job_code?: string;
  job_name?: string;
  client_name?: string;
  job_status?: string;
  [key: string]: unknown;
}

/* ── Analyst: who can be assigned, and to what ───────────────────────────── */

export async function getAnalystJobs(): Promise<AnalystJob[]> {
  return toArray<AnalystJob>(await unwrap(api.get('/api/analyst/jobs')));
}

export async function getEmployees(): Promise<AssignableEmployee[]> {
  return toArray<AssignableEmployee>(await unwrap(api.get('/api/analyst/employees')));
}

/** Employees with their current workload, used to pick an assignee. */
export async function getEmployeeAssignments(): Promise<AssignableEmployee[]> {
  return toArray<AssignableEmployee>(await unwrap(api.get('/api/analyst/employees/assignment')));
}

export async function getEmployeeSchedule(employeeId: number | string): Promise<any> {
  return unwrap(api.get(`/api/analyst/employee/${employeeId}/schedule`));
}

export interface AssignTaskPayload {
  department: string;
  employee_id?: number | string;
  priority?: string;
  note?: string;
  tasks?: Array<{ name: string; alloted_time: string; assigned_week: number }>;
}

export interface AssignSiteVisitPayload {
  department: 'Site Visit';
  site_engineer: number | string;
  assigned_date: string;
  assigned_shift: string;
  is_free_visit?: boolean;
  visit_count?: number;
  director_visit?: boolean;
  analyst_visit?: boolean;
}

/** `POST /api/analyst/job/{jobId}/assign` handles both tasks and site visits. */
export async function assignWork(
  jobId: number | string,
  payload: AssignTaskPayload | AssignSiteVisitPayload
): Promise<any> {
  return unwrap(api.post(`/api/analyst/job/${jobId}/assign`, payload));
}

/* ── The Assigned Work calendar ──────────────────────────────────────────── */

/** A task in the calendar's week lane. Carries a week, never a day. */
export interface WeekTask {
  id: number;
  task_name?: string | null;
  assigned_week?: number | null;
  alloted_time?: string | null;
  time_used?: string | null;
  priority?: string | null;
  status?: string | null;
  note?: string | null;
  job_id?: number | null;
  job_code?: string | null;
  job_name?: string | null;
  employee_id?: number | null;
  employee_name?: string | null;
}

/** A site visit in the calendar's day columns. Carries a date, so it has a day. */
export interface WeekVisit {
  id: number;
  assigned_date?: string | null;
  assigned_shift?: string | null;
  status?: string | null;
  note?: string | null;
  director_visit?: boolean | null;
  job_id?: number | null;
  job_code?: string | null;
  job_name?: string | null;
  site_address?: string | null;
  employee_id?: number | null;
  employee_name?: string | null;
}

export interface WeekAssignments {
  week: number;
  year: number;
  /** ISO dates for the Monday and Sunday the server counted, so the columns match it. */
  week_start: string;
  week_end: string;
  tasks: WeekTask[];
  visits: WeekVisit[];
}

const EMPTY_WEEK: WeekAssignments = {
  week: 0, year: 0, week_start: '', week_end: '', tasks: [], visits: [],
};

/**
 * Everything assigned in one ISO week, across every job.
 *
 * Tasks and visits arrive separately because they are scheduled differently: a visit has a
 * date and belongs on a day, a task has only a week number and belongs to the week.
 */
export async function getWeekAssignments(
  week: number,
  year: number
): Promise<WeekAssignments> {
  const body: any = await unwrap(
    api.get('/api/analyst/assignments/week', { params: { week, year } })
  );
  return body?.data ?? EMPTY_WEEK;
}

export async function getJobTasks(jobId: number | string): Promise<any[]> {
  return toNestedArray(await unwrap(api.get(`/api/analyst/job/${jobId}/tasks`)), 'tasks');
}

export async function getJobSiteVisits(jobId: number | string): Promise<SiteVisit[]> {
  return toNestedArray<SiteVisit>(
    await unwrap(api.get(`/api/analyst/job/${jobId}/site-visits`)),
    'visits'
  );
}

export async function getJobFiles(jobId: number | string): Promise<any[]> {
  return toNestedArray(await unwrap(api.get(`/api/analyst/job/${jobId}/files`)), 'files');
}

export async function editTask(
  taskId: number | string,
  payload: Record<string, unknown>
): Promise<any> {
  return unwrap(api.put(`/api/analyst/task/${taskId}/edit`, payload));
}

export async function deleteTask(taskId: number | string): Promise<any> {
  return unwrap(api.delete(`/api/analyst/task/${taskId}/delete`));
}

export async function getJobReport(jobId: number | string): Promise<any> {
  return unwrap(api.get(`/api/analyst/job-report/${jobId}`));
}

/* ── Employee: my site visits ────────────────────────────────────────────── */

export type SiteVisitType = 'upcoming' | 'completed';

export async function getMySiteVisits(
  employeeId: number | string,
  type: SiteVisitType
): Promise<SiteVisit[]> {
  return toArray<SiteVisit>(
    await unwrap(api.get(`/api/employee/${employeeId}/site-visits/${type}`))
  );
}

export async function getSiteVisitDetail(
  employeeId: number | string,
  visitId: number | string
): Promise<any> {
  return unwrap(api.get(`/api/employee/${employeeId}/site-visit/${visitId}`));
}

export interface CompleteSiteVisitPayload {
  date: string;
  time: string;
  report: string;
  name: string;
  report_image?: number;
  site_photos?: Array<{ image: number; comment?: string }>;
}

export async function completeSiteVisit(
  employeeId: number | string,
  visitId: number | string,
  payload: CompleteSiteVisitPayload
): Promise<any> {
  return unwrap(api.post(`/api/employee/${employeeId}/site-visit/${visitId}`, payload));
}

/** Point-of-contact details attached to a job. */
export async function getJobPoc(jobId: number | string): Promise<any> {
  return unwrap(api.get(`/api/employee/job/${jobId}/poc`));
}
